import classNames from 'classnames';
import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { createPrefixCls } from '../_utils/string';
import { ROW_HEIGHT } from './constants';
const prefixCls = createPrefixCls('picker');
export const PickerCol = ({ data, value, onChange }) => {
    const defaultIndex = useRef(0);
    const propIndex = data.findIndex((el) => el.value === (value === null || value === void 0 ? void 0 : value.value));
    const index = useMemo(() => {
        return propIndex === -1 ? defaultIndex.current : propIndex;
    }, [propIndex]);
    const initTranslateY = useMemo(() => -index * ROW_HEIGHT, [index]);
    const [pickerIndex, setPickerIndex] = useState(index);
    const [translateY, setTranslateY] = useState(initTranslateY);
    const [grabbing, setGrabbing] = useState(false);
    const baseY = useRef(0);
    const startY = useRef(0);
    const startTime = useRef(0);
    const timer = useRef(false);
    const $scroll = useRef();
    const minY = useMemo(() => -(data.length - 1) * ROW_HEIGHT, [data.length]);
    const maxY = useMemo(() => 0, []);
    useEffect(() => {
        setPickerIndex(index);
        setTranslateY(initTranslateY);
        return () => {
            timer.current = false;
        };
    }, [index, initTranslateY]);
    const getElementTranslateY = useCallback(() => {
        if ($scroll.current) {
            const currentTranslateY = $scroll.current.style.transform
                .replace(/\s+/g, '')
                .match(/translate3d\(0px,(.*)px,0px\)/);
            if (currentTranslateY) {
                return parseFloat(currentTranslateY[1]);
            }
            else {
                return 0;
            }
        }
        else {
            return 0;
        }
    }, [$scroll]);
    const setElementTranslateY = useCallback((Y) => {
        if ($scroll.current) {
            $scroll.current.style.transform = `translate3d(0px, ${Y}px, 0px)`;
        }
    }, [$scroll]);
    const getTranslateY = useCallback((target) => {
        if (target > maxY) {
            return maxY + (target - maxY) * 0.5;
        }
        else if (target < minY) {
            return minY + (target - minY) * 0.5;
        }
        else {
            return target;
        }
    }, [maxY, minY]);
    const easeOut = (t, b, c, d) => {
        return c * ((t = t / d - 1) * t * t * t * t + 1) + b;
    };
    const easeMove = useCallback((start, end, time, callback) => {
        const st = Date.now();
        const loop = () => {
            const t = Date.now() - st;
            const b = start;
            const c = end - start;
            const d = time;
            const cur = easeOut(t, b, c, d);
            if ((start > end && cur > end) || (start < end && cur < end)) {
                callback(cur, false);
                if (timer.current) {
                    requestAnimationFrame(loop);
                }
            }
            else {
                callback(end, true);
            }
        };
        loop();
    }, []);
    const getIndexByEnd = useCallback((end) => {
        if (end > maxY) {
            return 0;
        }
        else if (end < minY) {
            return data.length - 1;
        }
        else {
            return parseInt((Math.abs(end) / ROW_HEIGHT).toFixed(0));
        }
    }, [data.length, maxY, minY]);
    const moveToIndex = useCallback((index, time = 300) => {
        const start = getElementTranslateY();
        const end = -index * ROW_HEIGHT;
        easeMove(start, end, time, (cur, isEnd) => {
            setElementTranslateY(cur);
            if (isEnd) {
                setTranslateY(cur);
                if (pickerIndex !== index) {
                    setPickerIndex(index);
                    onChange && onChange(index);
                }
            }
        });
    }, [easeMove, getElementTranslateY, onChange, pickerIndex, setElementTranslateY]);
    const keepMove = useCallback((speed) => {
        const distance = (Math.abs(speed) / 0.003) * (speed < 0 ? -1 : 1);
        const end = getElementTranslateY() + distance;
        moveToIndex(getIndexByEnd(end), 1000);
    }, [getElementTranslateY, getIndexByEnd, moveToIndex]);
    const handlTouchStart = useCallback((e) => {
        timer.current = false;
        baseY.current = getElementTranslateY();
        const mouseEvent = e;
        const touchEvent = e;
        if (mouseEvent.clientY) {
            setGrabbing(true);
            startY.current = mouseEvent.clientY;
        }
        else {
            startY.current = touchEvent.touches[0].clientY;
        }
        startTime.current = Date.now();
    }, [getElementTranslateY]);
    const handlTouchMove = useCallback((e) => {
        e.preventDefault();
        let currentY;
        const mouseEvent = e;
        const touchEvent = e;
        if (mouseEvent.clientY) {
            if (!grabbing) {
                return;
            }
            currentY = mouseEvent.clientY;
        }
        else {
            currentY = touchEvent.touches[0].clientY;
        }
        setElementTranslateY(getTranslateY(baseY.current + currentY - startY.current));
    }, [getTranslateY, grabbing, setElementTranslateY]);
    const handlTouchEnd = useCallback((e) => {
        let endY;
        const mouseEvent = e;
        const touchEvent = e;
        if (mouseEvent.clientY) {
            setGrabbing(false);
            endY = mouseEvent.clientY;
        }
        else {
            endY = touchEvent.changedTouches[0].clientY;
        }
        const endTime = Date.now();
        const diffY = endY - startY.current;
        const diffTime = endTime - startTime.current;
        timer.current = true;
        if (diffTime < 150 && Math.abs(diffY) > 15) {
            const speed = diffY / diffTime;
            keepMove(speed);
        }
        else {
            moveToIndex(getIndexByEnd(getElementTranslateY()));
        }
    }, [getElementTranslateY, getIndexByEnd, keepMove, moveToIndex]);
    return (React.createElement("div", { className: classNames(prefixCls('col'), grabbing && prefixCls('grabbing')), onTouchStart: handlTouchStart, onTouchMove: handlTouchMove, onTouchEnd: handlTouchEnd, onMouseDown: handlTouchStart, onMouseMove: handlTouchMove, onMouseUp: handlTouchEnd, onMouseLeave: handlTouchEnd },
        React.createElement("div", { ref: $scroll, className: prefixCls('scroll'), style: {
                transform: `translate3d(0, ${translateY}px, 0)`
            } }, data.map((el) => {
            return (React.createElement("div", { className: prefixCls('row'), key: el.value }, el.text));
        }))));
};
export default PickerCol;
